import React, { useEffect, useState } from "react";
import { Card, Button } from "antd";
import moment from "moment";
import logo from "../../../../../../../apps/ui/masters-ui/src/app/spsa logo.png";

export interface QAQCProductCountPrintProps {
    record: any;
}

interface Row {
    slNo: number;
    weight: number;
    nm: number;
    dev: number;
    devSq: number;
}

export function QAQCProductCountPrint(props: QAQCProductCountPrintProps) {
    const [rows, setRows] = useState<Row[]>([]);
    const [summary, setSummary] = useState({
        avgWeight: 0,
        avgNm: 0,
        totalDevSq: 0,
        sd: 0,
        cv: 0,
        min: 0,
        max: 0,
    });

    useEffect(() => {
        if (props.record) calculate(props.record);
    }, [props.record]);

    const calculate = (record: any) => {
        const items = record?.items || [];
        if (items.length === 0) return;

        // Based on Excel: NM DEV = 100 / WT IN GMS
        const temp: Row[] = items.map((item: any, i: number) => {
            const weight = item.weightInGms || 0;
            const nm = weight > 0 ? 100 / weight : 0;
            return { slNo: i + 1, weight, nm, dev: 0, devSq: 0 };
        });

        const avgNm = temp.reduce((s, r) => s + r.nm, 0) / temp.length;
        const avgWeight = temp.reduce((s, r) => s + r.weight, 0) / temp.length;

        temp.forEach((r) => {
            // Based on Excel: M1 DEV = AVG NM DEV - NM DEV
            r.dev = Math.abs(avgNm - r.nm);
            // Based on Excel: M2 DEV = M1 DEV * M1 DEV
            r.devSq = r.dev * r.dev;
        });

        const totalDevSq = temp.reduce((s, r) => s + r.devSq, 0);
        // Based on Excel: SD = SQ RT [TOTAL M1 DEV / (TOTAL SL NO - 1)]
        const sd = temp.length > 1 ? Math.sqrt(totalDevSq / (temp.length - 1)) : 0;
        // Based on Excel: CV% = SD / AVG NM DEV * 100
        const cv = avgNm !== 0 ? (sd / avgNm) * 100 : 0;

        const weights = temp.map((r) => r.weight);

        setRows(temp);
        setSummary({
            avgWeight,
            avgNm,
            totalDevSq,
            sd,
            cv,
            min: Math.min(...weights),
            max: Math.max(...weights),
        });
    };

    const printPage = () => {
        const content = document.getElementById("print-area");
        if (!content) return;

        const win = window.open("", "", "width=900,height=700");

        if (win) {
            win.document.write(`
      <html>
      <head>
      <title>Product Count Certificate</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: 12px; 
          color: black;
          margin: 0;
          padding: 0;
        }
        #print-container {
          width: 100%;
          padding: 20px;
          box-sizing: border-box;
        }
        .header-section {
          text-align: center;
          margin-bottom: 20px;
          width: 100%;
        }
        .header-section h1 {
          margin: 0;
          font-size: 20px;
          font-weight: bold;
        }
        .header-section h2 {
          margin: 5px 0 0 0;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
        }
        .meta-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          width: 100%;
          font-weight: bold;
        }
        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        .print-table th, .print-table td {
          border: 1px solid black;
          padding: 6px 4px;
          text-align: center;
          font-size: 11px;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          font-weight: bold;
          width: 100%;
        }
        .stats-section {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
          font-weight: bold;
          width: 100%;
        }
        .stats-col {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .footer-section {
          margin-top: 80px;
          display: flex;
          justify-content: space-between;
          padding: 0 10px;
          font-weight: bold;
          width: 100%;
        }
        .footer-section div {
          width: 25%;
          text-align: center;
        }
        .logo-container {
          margin-bottom: 10px;
          text-align: center;
        }
        .logo-container img {
          height: 50px;
        }
      </style>
      </head>
      <body>
        <div id="print-container">${content.innerHTML}</div>
      </body>
      </html>
      `);
            win.document.close();
            setTimeout(() => {
                win.print();
                win.close();
            }, 500);
        }
    };

    const h = props.record;

    return (
        <Card
            title="PRODUCT COUNT CERTIFICATE"
            extra={<Button type="primary" onClick={printPage}>Print</Button>}
            headStyle={{ textAlign: "center" }}
        >
            <style>{`
        #print-area {
          font-family: Arial, sans-serif;
          color: black;
          padding: 20px;
        }
        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        .print-table th, .print-table td {
          border: 1px solid black;
          padding: 6px 4px;
          text-align: center;
        }
        .header-section {
          text-align: center;
          margin-bottom: 20px;
        }
        .meta-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          font-weight: bold;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          font-weight: bold;
        }
        .stats-section {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
          font-weight: bold;
        }
        .stats-col {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .footer-section {
        margin-top: 80px;
        display: flex;
        justify-content: space-between;
        padding: 0 10px;
        font-weight: bold;
        }

        .footer-section div {
        width: 25%;
        text-align: center;
}
      `}</style>

            <div id="print-area">
                <div className="header-section">
                    <div className="logo-container">
                        {/* <img src={logo} style={{ height: 50 }} alt="Logo" /> */}
                    </div>
                    <h1 style={{ margin: 0, fontSize: 20 }}>SPSA SILKS PVT LTD</h1>
                    <h2 style={{ margin: "5px 0 0 0", fontSize: 16, textDecoration: "underline" }}>SLIVER C. V.%</h2>
                </div>

                <div className="meta-row">
                    <div>DATE: {moment(h.testDate).format("D/MM/YY")}</div>
                    <div>COUNT: {h.items?.[0]?.count || "0"}G</div>
                </div>

                <div className="meta-row">
                    <div>LOT NO: {h.lotNumber}</div>
                    <div>PROCESS: FINAL D/F</div>
                </div>

                <table className="print-table">
                    <thead>
                        <tr>
                            <th style={{ width: "8%" }}>SL NO.</th>
                            <th style={{ width: "23%" }}>WT IN GRMS</th>
                            <th style={{ width: "23%" }}>NM DEVIATION(X)</th>
                            <th style={{ width: "23%" }}>MEAN DEVIATION(X-X̄)</th>
                            <th style={{ width: "23%" }}>MEAN DEVIATION (X-X̄)²</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.slNo}>
                                <td>{r.slNo}</td>
                                <td>{r.weight ? Number(Number(r.weight).toFixed(2)) : '-'}</td>
                                <td>{r.nm ? Number(Number(r.nm).toFixed(3)) : '-'}</td>
                                <td>{r.dev ? Number(Number(r.dev).toFixed(3)) : '-'}</td>
                                <td>{r.devSq ? Number(Number(r.devSq).toFixed(6)) : '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <table className="print-table" style={{ borderTop: "none" }}>
                    <tbody>
                        <tr>
                            <td style={{ width: "8%", border: "none" }}></td>

                            <td style={{ width: "23%", border: "none", textAlign: "center", fontWeight: "bold" }}>
                                AVG: {summary.avgWeight ? Number(Number(summary.avgWeight).toFixed(2)) : '-'}G
                            </td>

                            <td style={{ width: "23%", border: "none", textAlign: "center", fontWeight: "bold" }}>
                                AVG: {summary.avgNm ? Number(Number(summary.avgNm).toFixed(3)) : '-'}NM
                            </td>

                            <td style={{ width: "23%", border: "none" }}></td>

                            <td style={{ width: "23%", border: "none", textAlign: "center", fontWeight: "bold" }}>
                                TOTAL: {summary.totalDevSq ? Number(Number(summary.totalDevSq).toFixed(6)) : '-'}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="stats-section">
                    <div className="stats-col">
                        <div>MIN: {summary.min ? Number(Number(summary.min).toFixed(2)) : '-'}G</div>
                        <div>MAX: {summary.max ? Number(Number(summary.max).toFixed(2)) : '-'}G</div>
                    </div>
                    <div className="stats-col" style={{ textAlign: "right" }}>
                        <div>SD: {summary.sd ? Number(Number(summary.sd).toFixed(6)) : '-'}</div>
                        <div>CV%: {summary.cv ? Number(Number(summary.cv).toFixed(2)) : '-'}%</div>
                    </div>
                </div>

                <div className="footer-section">
                    <div>Q. CONTROL</div>
                    <div>SUPERVISOR</div>
                    <div>HOD</div>
                    <div>MD</div>
                </div>
            </div>
        </Card>
    );
}

export default QAQCProductCountPrint;
